import { useRequest } from 'ahooks';
import { AxiosError } from 'axios';
import { Button } from 'components/ui/button';
import { Carousel, CarouselApi, CarouselContent, CarouselDots, CarouselItem } from 'components/ui/carousel';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/ui/form';
import { Progress } from 'components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Spinner } from 'components/ui/spinner';
import { Textarea } from 'components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { cn } from 'lib/utils';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import { OnboardingSurveyV2, PatientFile } from 'services/api/models/SymptomTrackingDTO';
import { routes } from 'services/RouteService';
import { selectCurrentUser } from 'store/users/selectors';

// The whole survey sits on the deep teal (`inverse`) surface: answers are
// mint pills that turn white once chosen, calls to action are white pills.
// The onboarding questionnaire sits on the inverse background: its controls take the card and mint fills.
const whiteButtonClassName = 'h-12 bg-card text-foreground hover:bg-card/90';
const mintButtonClassName = 'h-12 bg-success text-inverse-foreground hover:bg-success/90';
const answerButtonClassName = (selected: boolean) =>
  cn('mb-5 w-full whitespace-normal', selected ? whiteButtonClassName : mintButtonClassName);
const toggleItemClassName =
  'h-10 flex-1 border-0 bg-success text-base text-inverse-foreground hover:bg-success/90 hover:text-inverse-foreground data-[state=on]:bg-card data-[state=on]:text-foreground';

const sectionClassName = 'flex w-full justify-center px-4 py-2.5 text-center';
const answersContainerClassName = 'mx-auto flex w-4/5 flex-col px-4 py-2.5';

const BIRTH_YEARS = Array.from({ length: new Date().getFullYear() - 1900 }, (_, index) => `${1900 + index}`).reverse();

/** The patient file as the form holds it: the year is the `Select`'s string value. */
type PatientFileFormValues = {
  birth_year: string;
  takes_contraception?: boolean;
  has_children?: boolean;
  is_pregnant?: boolean;
  is_trying_to_conceive?: boolean;
  is_taking_treatment?: boolean;
};

type PatientFileBooleanField = Exclude<keyof PatientFileFormValues, 'birth_year'>;

const toToggleValue = (value: unknown) => (value === true ? 'true' : value === false ? 'false' : '');

const useMultiSelect = () => {
  const [values, setValues] = useState(new Set<string>());

  function isValueSelected(value: string) {
    return values.has(value);
  }

  function toggleValue(symptom: string) {
    if (isValueSelected(symptom)) setValues(oldValues => new Set([...oldValues].filter(item => item !== symptom)));
    else setValues(oldValues => new Set([...oldValues, symptom]));
  }

  function renderButton(valueKey: string, label: string) {
    const selected = isValueSelected(valueKey);
    return (
      <Button
        key={valueKey}
        selected={selected}
        className={answerButtonClassName(selected)}
        onClick={() => toggleValue(valueKey)}
      >
        {label}
      </Button>
    );
  }

  return { values, renderButton, setValues };
};

const SymptomTrackingSurvey: React.FC = () => {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector(selectCurrentUser);

  const [carousel, setCarousel] = useState<CarouselApi>();

  const isEditingOnboarding = Boolean((location.state as { editOnboarding?: boolean } | null)?.editOnboarding);

  const pathologiesOptions = [
    'ENDOMETRIOSIS',
    'SOPK',
    'FIBROME',
    'ADENOMYOMATOSIS',
    'BREAST_CANCER',
    'UTERUS_CANCER',
    'PMS',
  ];

  const {
    values: pathologiesValues,
    renderButton: renderPathologyButton,
    setValues: setPathologiesValues,
  } = useMultiSelect();

  const [showPathologiesField, setShowPathologiesField] = useState(false);
  const [extraPathologiesValue, setExtraPathologiesValue] = useState('');

  const patientFileForm = useForm<PatientFileFormValues>({ defaultValues: { birth_year: '' } });

  // Question 2: Chronic symptoms
  const [chronicSymptomDuration, setChronicSymptomDuration] = useState<
    'NO' | 'LESS_THAN_ONE_YEAR' | 'ONE_TO_THREE_YEARS' | 'MORE_THAN_THREE_YEARS' | null
  >(null);
  const [symptomImpact, setSymptomImpact] = useState<
    'NONE' | 'LOW' | 'MODERATE' | 'IMPORTANT' | 'VERY_IMPORTANT' | null
  >(null);
  const [consultationCount, setConsultationCount] = useState<'NO' | 'ONE_TO_THREE' | 'MORE_THAN_THREE' | null>(null);

  const { run: loadPatientFile, loading: loadingPatientFile } = useRequest(
    () => ApiSymptomTrackingManager.getPatientFileMe(),
    {
      manual: true,
      onSuccess: file => {
        // Convert birth_year from number to string for the Select component
        patientFileForm.reset({
          ...file,
          birth_year: file.birth_year ? String(file.birth_year) : '',
        } as unknown as PatientFileFormValues);
      },
      onError: error => {
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          // ignore 404 errors as not all users have filled out the patient file
          // for other errors, log for debugging
          console.error(error);
        }
      },
    },
  );

  const { run: loadOnboardingSurveyV2, loading: loadingOnboardingSurveyV2 } = useRequest(
    () => ApiSymptomTrackingManager.getOnboardingSurveyV2Me(),
    {
      manual: true,
      onSuccess: survey => {
        setPathologiesValues(new Set(survey.pathologies));
        if (survey.extra_pathologies) {
          setShowPathologiesField(true);
          setExtraPathologiesValue(survey.extra_pathologies);
        }
        setChronicSymptomDuration(survey.chronic_symptom_duration ?? null);
        setSymptomImpact(survey.symptom_impact ?? null);
        setConsultationCount(survey.consultation_count ?? null);
      },
      onError: error => {
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          // ignore 404 errors as not all users have filled out the onboarding survey v2
          // for other errors, log for debugging
          console.error(error);
        }
      },
    },
  );

  useEffect(() => {
    loadPatientFile();
    loadOnboardingSurveyV2();
  }, [loadOnboardingSurveyV2, loadPatientFile]);

  const nextPage = () => {
    carousel?.scrollNext();
  };

  const { loading: postingOnboardingSurvey, run: postOnboardingSurvey } = useRequest(
    () => {
      const params: OnboardingSurveyV2 = {
        pathologies: Array.from(pathologiesValues),
        extra_pathologies: extraPathologiesValue || null,
        chronic_symptom_duration: chronicSymptomDuration,
        symptom_impact: chronicSymptomDuration && chronicSymptomDuration !== 'NO' ? symptomImpact : null,
        consultation_count: chronicSymptomDuration && chronicSymptomDuration !== 'NO' ? consultationCount : null,
      };
      return ApiSymptomTrackingManager.postOnboardingSurveyV2(params);
    },
    {
      manual: true,
      loadingDelay: 300,
      onSuccess: () => {
        if (isEditingOnboarding) {
          navigate(routes.settings.route);
        }
      },
      onError: e => {
        // TODO error handling
        console.error(e);
      },
    },
  );

  const { loading: postingPatientFile, run: postPatientFile } = useRequest(
    (formValues: PatientFile) => {
      return ApiSymptomTrackingManager.postPatientFile(formValues);
    },
    {
      manual: true,
      loadingDelay: 300,
    },
  );

  const renderProgress = (step: number, total: number) => {
    return (
      <Progress
        value={100 * (step / total)}
        className="bg-success [&>[data-slot=progress-indicator]]:bg-inverse-foreground"
      />
    );
  };

  const requiredFieldMessage = t('onboarding.questionnaire.file.requiredField');

  const renderYesNoQuestion = (name: PatientFileBooleanField, label: string) => (
    <>
      <p className="mb-1">{label}</p>
      <FormField
        control={patientFileForm.control}
        name={name}
        rules={{ validate: value => value === true || value === false || requiredFieldMessage }}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <ToggleGroup
                id={name}
                type="single"
                variant="outline"
                className="w-full"
                value={toToggleValue(field.value)}
                onValueChange={value => {
                  if (value) field.onChange(value === 'true');
                }}
              >
                <ToggleGroupItem value="true" className={toggleItemClassName}>
                  {t('onboarding.questionnaire.yes')}
                </ToggleGroupItem>
                <ToggleGroupItem value="false" className={toggleItemClassName}>
                  {t('onboarding.questionnaire.no')}
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            {/* the questionnaire sits on the inverse background */}
            <FormMessage className="text-left text-inverse-foreground" />
          </FormItem>
        )}
      />
    </>
  );

  const carouselPages = [
    {
      content: (
        <>
          <div className={sectionClassName}>
            <div>
              <h3>
                {t('onboarding.questionnaire.greeting')}{' '}
                <span className="text-info-muted">{currentUser?.first_name}</span>, <br />
                <br />
              </h3>

              <p>{t('onboarding.questionnaire.description')}</p>
            </div>
          </div>
          <div className={answersContainerClassName}>
            <Button className={whiteButtonClassName} onClick={() => nextPage()}>
              {t('onboarding.questionnaire.start')}
            </Button>
          </div>
        </>
      ),
    },

    {
      content: (
        <>
          <div className={`${sectionClassName} flex-col`}>
            {renderProgress(1, 4)}
            <h3>{t('onboarding.questionnaire.file.title')}</h3>
          </div>
          <div className="flex w-full flex-col items-start justify-start px-4 py-2.5 text-left">
            <Form {...patientFileForm}>
              <form
                className="w-full"
                onSubmit={patientFileForm.handleSubmit(formValues => {
                  // Convert birth_year from string (Select value) back to number for API
                  // formValues.birth_year is guaranteed to be present due to form validation
                  postPatientFile({
                    ...formValues,
                    birth_year: Number(formValues.birth_year),
                  } as unknown as PatientFile);
                  posthog.capture('onboarding_questionnaire.patient_file_submitted');
                  nextPage();
                })}
              >
                <p className="mb-1">{t('onboarding.questionnaire.file.birthYear')}</p>
                <FormField
                  control={patientFileForm.control}
                  name="birth_year"
                  rules={{ required: requiredFieldMessage }}
                  render={({ field }) => (
                    <FormItem>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger id="birth_year" className="w-full data-[size=default]:h-10">
                            <SelectValue placeholder={t('onboarding.questionnaire.file.birthYearPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {BIRTH_YEARS.map(year => (
                            <SelectItem key={year} value={year}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage className="text-inverse-foreground" />
                    </FormItem>
                  )}
                />

                {renderYesNoQuestion('takes_contraception', t('onboarding.questionnaire.file.doYouTakeContraception'))}
                {renderYesNoQuestion('has_children', t('onboarding.questionnaire.file.doYouHaveChildren'))}
                {renderYesNoQuestion('is_pregnant', t('onboarding.questionnaire.file.areYouPregnant'))}
                {renderYesNoQuestion(
                  'is_trying_to_conceive',
                  t('onboarding.questionnaire.file.areYouTryingToConceive'),
                )}
                {renderYesNoQuestion(
                  'is_taking_treatment',
                  t('onboarding.questionnaire.file.areYouTakingAnyMedication'),
                )}
                <div className="h-4 w-full" />
                <div className="h-4 w-full" />
                <Button
                  size="xl"
                  className={cn('w-full', whiteButtonClassName)}
                  type="submit"
                  loading={postingPatientFile}
                >
                  {t('onboarding.questionnaire.continue')}
                </Button>
              </form>
            </Form>
          </div>
        </>
      ),
    },

    {
      content: (
        <>
          <div className={sectionClassName}>
            <div className="w-full">
              {renderProgress(2, 4)}
              <h3>{t('onboarding.questionnaire.pathologies.question')}</h3>
            </div>
          </div>
          <div className={answersContainerClassName}>
            {pathologiesOptions.map(pathology =>
              renderPathologyButton(pathology, t(`onboarding.questionnaire.pathologies.options.${pathology}`)),
            )}
            {showPathologiesField && (
              <Textarea
                rows={5}
                autoCorrect="on"
                placeholder={t('onboarding.questionnaire.pathologies.otherPathologiesPlaceholder')}
                value={extraPathologiesValue}
                onChange={e => setExtraPathologiesValue(e.target.value)}
              />
            )}
            {!showPathologiesField && (
              <Button
                variant="ghost"
                className="text-inverse-foreground hover:bg-inverse-foreground/10 hover:text-inverse-foreground"
                onClick={() => setShowPathologiesField(true)}
              >
                {t('onboarding.questionnaire.pathologies.otherPathologies')}
              </Button>
            )}
          </div>
          <div className={sectionClassName}>
            <Button
              className={whiteButtonClassName}
              onClick={() => {
                nextPage();
              }}
            >
              {showPathologiesField || pathologiesValues.size > 0
                ? t('onboarding.questionnaire.pathologies.savePathologies')
                : t('onboarding.questionnaire.pathologies.noPathologies')}
            </Button>
          </div>
        </>
      ),
    },

    {
      content: (
        <>
          <div className={sectionClassName}>
            <div className="w-full">
              {renderProgress(3, 4)}
              <h3>{t('onboarding.questionnaire.chronicSymptoms.question')}</h3>
              <p>{t('onboarding.questionnaire.chronicSymptoms.description')}</p>
            </div>
          </div>
          <div className={answersContainerClassName}>
            {(['NO', 'LESS_THAN_ONE_YEAR', 'ONE_TO_THREE_YEARS', 'MORE_THAN_THREE_YEARS'] as const).map(option => (
              <Button
                key={option}
                selected={chronicSymptomDuration === option}
                className={answerButtonClassName(chronicSymptomDuration === option)}
                onClick={() => {
                  setChronicSymptomDuration(option);
                  if (option === 'NO') {
                    setSymptomImpact(null);
                    setConsultationCount(null);
                  }
                }}
              >
                {t(`onboarding.questionnaire.chronicSymptoms.options.${option}`)}
              </Button>
            ))}
          </div>

          {chronicSymptomDuration && chronicSymptomDuration !== 'NO' && (
            <>
              <div className={sectionClassName}>
                <div>
                  <h4 className="mt-6">{t('onboarding.questionnaire.chronicSymptoms.impact.question')}</h4>
                </div>
              </div>
              <div className={answersContainerClassName}>
                {(['NONE', 'LOW', 'MODERATE', 'IMPORTANT', 'VERY_IMPORTANT'] as const).map(option => (
                  <Button
                    key={option}
                    selected={symptomImpact === option}
                    className={answerButtonClassName(symptomImpact === option)}
                    onClick={() => setSymptomImpact(option)}
                  >
                    {t(`onboarding.questionnaire.chronicSymptoms.impact.options.${option}`)}
                  </Button>
                ))}
              </div>

              <div className={sectionClassName}>
                <div>
                  <h4 className="mt-6">{t('onboarding.questionnaire.chronicSymptoms.consultation.question')}</h4>
                </div>
              </div>
              <div className={answersContainerClassName}>
                {(['NO', 'ONE_TO_THREE', 'MORE_THAN_THREE'] as const).map(option => (
                  <Button
                    key={option}
                    selected={consultationCount === option}
                    className={answerButtonClassName(consultationCount === option)}
                    onClick={() => setConsultationCount(option)}
                  >
                    {t(`onboarding.questionnaire.chronicSymptoms.consultation.options.${option}`)}
                  </Button>
                ))}
              </div>
            </>
          )}

          <div className={sectionClassName}>
            <Button
              className={whiteButtonClassName}
              loading={postingOnboardingSurvey}
              onClick={() => {
                // Require chronic_symptom_duration to be selected
                if (!chronicSymptomDuration) {
                  return;
                }
                // If user selected a "yes" option, require impact and consultation
                if (chronicSymptomDuration !== 'NO') {
                  if (!symptomImpact || !consultationCount) {
                    return;
                  }
                }
                postOnboardingSurvey();
                posthog.capture('onboarding_questionnaire.onboarding_survey_submitted');
                if (!isEditingOnboarding) {
                  nextPage();
                }
                // When editing, navigation to settings happens in onSuccess callback
              }}
            >
              {t('onboarding.questionnaire.chronicSymptoms.continue')}
            </Button>
          </div>
        </>
      ),
    },

    {
      content: (
        <>
          <div className={sectionClassName}>
            <div>
              <div className="h-4 w-full" />
              <div className="h-4 w-full" />
              <h3>
                {t('onboarding.questionnaire.congratulations')},{' '}
                <span className="text-info-muted">{currentUser?.first_name}</span> ! <br />
              </h3>
              <div className="h-4 w-full" />
              <div className="h-4 w-full" />

              <p className="m-0 text-lg">
                <Trans i18nKey="onboarding.questionnaire.postFile.description" components={{ bold: <strong /> }} />
              </p>
            </div>
          </div>
          <div className={answersContainerClassName}>
            <div className="h-4 w-full" />
            <div className="h-4 w-full" />
            <Button
              className={whiteButtonClassName}
              onClick={() => {
                navigate(routes.home.route);
              }}
            >
              {t('onboarding.questionnaire.postFile.letsGo')}
            </Button>
          </div>
        </>
      ),
    },
  ];

  const effectiveCarouselPages = isEditingOnboarding ? carouselPages.slice(1, -1) : carouselPages;

  const renderOnboardingCarouselPageTemplate = (
    index: number,
    children: { content?: React.ReactElement; scrollableContent?: React.ReactElement },
  ) => {
    return (
      <CarouselItem key={index} className="h-full overflow-y-auto pb-16 pl-0">
        {children.content && (
          <div className="flex flex-none flex-col items-center px-2.5 pt-2.5 pb-16 text-center">{children.content}</div>
        )}
      </CarouselItem>
    );
  };

  const isLoadingProfile = loadingPatientFile || loadingOnboardingSurveyV2;

  return (
    <div className="h-app-screen bg-inverse text-inverse-foreground">
      {isLoadingProfile ? (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <Spinner className="size-10 text-inverse-foreground" />
          <p className="text-inverse-foreground">{t('common.loading')}</p>
        </div>
      ) : (
        <Carousel
          setApi={setCarousel}
          opts={{ loop: false }}
          className="h-full bg-inverse [&_[data-slot=carousel-content]]:h-full"
        >
          <CarouselContent className="ml-0 h-full">
            {effectiveCarouselPages.map((page, index) => renderOnboardingCarouselPageTemplate(index, page))}
          </CarouselContent>
          <CarouselDots className="absolute inset-x-0 bottom-5 [&>button]:bg-inverse-foreground/40 [&>button[data-active=true]]:bg-inverse-foreground" />
        </Carousel>
      )}
    </div>
  );
};

export default SymptomTrackingSurvey;
